import {
  EncounterStatus,
  MedicationStatus,
  OrderType,
} from "../services/utils";

export class Procedure {
  id: string;
  encounterId: string;
  region: string = "";
  physicianName: string;
  patientStatus: string = "";
  area: string = "";
  procedureType: string = "";
  side: string = "";
  location: string = "";
  additionalProcedureNeeded: boolean;
  additionalProcedureNeededDate: string;
  /** Procedure list Today tab status (PHI `procedure.chartsStatus`). */
  chartsStatus?: string | null;
  /** Procedure room workflow step in progress (list-clinic / encounterProcedure). */
  procedureRoomCurrentStep?: string;
  /** Completed procedure room steps; last entry is the list Status column value. */
  procedureRoomCompletedSteps?: string[];
  /** Time Out initiated at (HH:mm) when recorded for this procedure. */
  procedureTimeout?: string;
  constructor(appointmentId: string) {
    this.encounterId = appointmentId;
  }
}

export interface RiskFactorQuestion {
  id: string;
  question: string;
  answer: string;
}

export class RiskFactor {
  id: string;
  procedureId: string;
  encounterId: string;
  aps: boolean;
  pad: boolean;
  cad: boolean;
  hypertension: boolean;
  hyperlipidemia: boolean;
  diabetes: boolean;
  diabetesType: string;
  isSmoker: boolean;
  exSmoker: boolean;
  isHeartFailure: boolean;
  constructor(procedureId: string, encounterId: string) {
    this.procedureId = procedureId;
    this.encounterId = encounterId;
  }
}

export class UltrasoundFinding {
  id?: string;
  procedureId: string;
  patientId: string;
  region: string;
  area?: string;
  side?: string;
  location?: string;
  percentStenosis?: string;
  compReflex?: string;
  positiveNegative?: string;
  seconds?: string;
  constructor(patientId: string, procedureId: string) {
    this.procedureId = procedureId;
    this.patientId = patientId;
    this.region = "";
  }
}

export class Vitals {
  ekgRythm: string;
  temp: string;
  oxygenLpm: string;
  painStatus: string;
  bp: string;
  hr: string;
  soO2: string;
  resp: string;
  fluidIntake: string;
  type: string;
  ivLocation: string;
  angicathGauage: string;
  fluid: string;
  rate: string;
  id: string;
  time: string;
  procedureId: string;
  updatedBy?: string;
  createdAt?: string | Date;
  updatedAt?: string | Date;
  /** Height (e.g. inches) — optional; used by Engage prospect biometrics and encounter vitals. */
  ht?: string;
  /** Weight (e.g. lb) — optional. */
  wt?: string;
  constructor(procedureId: string, type: EncounterStatus) {
    this.type = type;
    this.procedureId = procedureId;
  }
}

export class BloodGlucose {
  id: string;
  time: string;
  name: string;
  type: string;
  procedureId: string;
  /** Linked PHI clinical order (procedure room Orders drawer). */
  clinicalOrderId?: string;
  /** When the order was placed (Sent), same semantics as lab `whenDate`. */
  whenDate?: string;
  updatedBy?: string;
  updatedAt?: string;

  constructor(procedureId: string, type: EncounterStatus) {
    this.type = type;
    this.procedureId = procedureId;
  }
}
export class DispenseSupply {
  id?: string;
  procedureId: string;
  supplyId: string;
  type: string;
  name?: string;
  qty?: string;

  constructor(procedureId: string, type: EncounterStatus) {
    this.procedureId = procedureId;
    this.type = type;
  }
}
export class SnacksAndMeals {
  id?: string;
  name: string;
  snackId: string;
  qty: number;
  type: string;
  procedureId: string;
  constructor(procedureId: string, type: EncounterStatus) {
    this.procedureId = procedureId;
    this.type = type;
  }
}

export class EncounterSchedule {
  id: string;
  encounterId: string;
  procedureRoom: string;
  bedNo: string;
  scheduleDate: string;
  actualCheckIn?: string;
  checkIn: string;
  preProcedureMin: number;
  procedureTime: string;
  procedureMin: number;
  checkOut: string;
  checkOutMin: number;
  checkInOutStatus: string;
  scheduleStatus: string;
  patientName: string;
  visitType: string;
  constructor(encounterId: string, date: string = undefined) {
    this.encounterId = encounterId;
    this.scheduleDate = date;
  }
  scheduleResourceId: string;
  /** Anthropometrics from `PATCH .../encounters/patient/{id}` (`schedulePatient`); height in., weight lb when from vitals flow. */
  ht?: string;
  wt?: string;
  measuredOn?: string;
}

export interface LabDetails {
  calcium: number | "";
  creatinine: number | "";
  sodium: number | "";
  bun: number | "";
  hemoglobin: number | "";
  potassium: number | "";
  anGap: number | "";
  carbonDioxide: number | "";
  glucose: number | "";
  chloride: number | "";
  hgb: number | "";
  hct: number | "";
  coumadin: boolean;
  inr: boolean;
  eGFR: number | "";
  maxContrast: number | "";
}
export class ModifiedAldreteScoring {
  id: string;
  procedureId: string;
  activityMoveable: string;
  respiration: string;
  type: string;
  circulation: string;
  consciousness: string;
  o2saturation: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  constructor(procedureId: string, type: EncounterStatus) {
    this.procedureId = procedureId;
    this.type = type;
    this.activityMoveable = "";
    this.respiration = "";
    this.circulation = "";
    this.consciousness = "";
    this.o2saturation = "";
  }
}

export class LeadEKG {
  id: string;
  procedureId: string;
  type: string;
  fileName: string;
  fileUrl: string;
  signedLink: string;
  interpetation: string;
  signedStatus: string;
  imageData: {
    base64String: string;
    contentType: string;
  };
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  constructor(procedureId: string, type: EncounterStatus) {
    this.procedureId = procedureId;
    this.type = type;
  }
}

export class Medication {
  id?: string;
  patientId?: string;
  procedureId?: string;
  encounterId?: string;
  action?: string;
  name?: string;
  strength?: string;
  route?: string;
  form?: string;
  commonName?: string;
  activeingredient?: string;
  orderType?: OrderType;
  drugCode?: string;
  frequency?: string;
  updatedAt?: string;
  isActive?: boolean;
  status?: MedicationStatus;
  lastTakenDateTime?: string;
  actionId?: number;
  dosage?: string;
  startDate?: string;
  endDate?: string;
  notes?: string;
  cancellationReason?: string;
  clinicalStatusReason?: string;
  type?: string;
  medActionStatus?: number;
  isHold?: boolean;
  holdConfirmed?: boolean;
  holdNotRequired?: boolean;
  existing?: boolean;
  constructor(patientId: string, encounterId: string, type: EncounterStatus) {
    this.patientId = patientId;
    this.encounterId = encounterId;
    this.type = type;
  }
}

export class ChiefComplaint {
  id: string;
  patientId: string;
  encounterId: string;
  description: string;
  duration: string;
  severity: string;
  notes: string;
  updatedBy: string;
  updatedAt: string;
  createdAt: string;
  treatmentFor: string;
  isVerified: boolean;
  constructor(patientId: string, encounterId: string) {
    this.patientId = patientId;
    this.encounterId = encounterId;
  }
}
